import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Grid, IconButton, useMediaQuery } from '@mui/material';
import Typography from '@mui/material/Typography';
import { FieldArray, useFormikContext } from 'formik';
import { Add, Delete } from '@mui/icons-material';
import { theme } from '../../../common/GeneralUtils';
import { LabelData, MultipleTicketsEditInput, OrderPageData, PartialLabelData } from '../../../api';
import { FormTextField } from '../../formComponents/FormTextField';
import { FormLabelField } from '../../formComponents/FormLabelField';
import { FormCheckbox } from '../../formComponents/FormCheckBox';
import {
  activePriceOptions,
  activeTickets,
  findMultipleTicketsPrice,
  findMultipleTicketsUiObject,
  generateTicketId,
  MULTIPLE_TICKETS_PRICE_TYPE,
  MULTIPLE_TICKETS_UI_OBJECT_TYPE,
} from '../../../common/multipleTicketsUtils';
import ConfirmationDialog from '../../../common/ui/ConfimationDialog';

export type TicketFormValue = {
  id: string;
  label: PartialLabelData;
  default_ticket_group_size?: number;
  capacity?: number;
  counts_toward_total_capacity: boolean;
  price_per_unit: number;
};

export type MultipleTicketsFormValues = {
  uiObjectId: string;
  label: PartialLabelData;
  external_label?: PartialLabelData;
  min_total_participants: number;
  max_total_participants: number;
  dashboard_no_limit?: boolean;
  min_total_alert?: PartialLabelData;
  max_total_alert?: PartialLabelData;
  tickets: TicketFormValue[];
};

export const buildLabel = (partial: PartialLabelData | undefined, languages: string[]): LabelData =>
  Object.fromEntries(
    languages.map((lang) => [lang, (partial ?? {})[lang as keyof PartialLabelData] || '']),
  ) as LabelData;

export const initMultipleTicketsForm = (orderPage: OrderPageData): MultipleTicketsFormValues | undefined => {
  const uiObject = findMultipleTicketsUiObject(orderPage);
  const price = findMultipleTicketsPrice(orderPage);
  if (!uiObject && !price) return undefined;

  const priceByTicketId = new Map(activePriceOptions(price).map((opt) => [opt.ticket_id, opt.price_per_unit]));

  if (!uiObject) {
    return {
      uiObjectId: generateTicketId(),
      label: {},
      min_total_participants: 0,
      max_total_participants: 0,
      tickets: [],
    };
  }

  return {
    uiObjectId: uiObject.id,
    label: uiObject.label || {},
    external_label: uiObject.external_label,
    min_total_participants: uiObject.min_total_participants ?? 0,
    max_total_participants: uiObject.max_total_participants ?? 0,
    dashboard_no_limit: uiObject.dashboard_no_limit,
    min_total_alert: uiObject.min_total_alert,
    max_total_alert: uiObject.max_total_alert,
    tickets: activeTickets(uiObject).map((t) => ({
      id: t.id,
      label: t.label || {},
      default_ticket_group_size: t.default_ticket_group_size,
      capacity: t.capacity,
      counts_toward_total_capacity: t.counts_toward_total_capacity !== false,
      price_per_unit: priceByTicketId.get(t.id) ?? 0,
    })),
  };
};

export const buildMultipleTicketsInput = (
  values: MultipleTicketsFormValues,
  languages: string[],
): MultipleTicketsEditInput => ({
  multiple_tickets_ui_object_data: {
    id: values.uiObjectId,
    type: MULTIPLE_TICKETS_UI_OBJECT_TYPE,
    label: buildLabel(values.label, languages),
    external_label: values.external_label ? buildLabel(values.external_label, languages) : undefined,
    min_total_participants: values.min_total_participants || 0,
    max_total_participants: values.max_total_participants || 0,
    dashboard_no_limit: values.dashboard_no_limit,
    min_total_alert: values.min_total_alert ? buildLabel(values.min_total_alert, languages) : undefined,
    max_total_alert: values.max_total_alert ? buildLabel(values.max_total_alert, languages) : undefined,
    tickets: values.tickets.map((t) => ({
      id: t.id,
      label: buildLabel(t.label, languages),
      default_ticket_group_size: t.default_ticket_group_size,
      capacity: t.capacity,
      counts_toward_total_capacity: t.counts_toward_total_capacity,
    })),
  },
  multiple_tickets_price_data: {
    type: MULTIPLE_TICKETS_PRICE_TYPE,
    // @ts-ignore - MultipleTicketsPriceData uses the same `custom` data_type as other price rows
    data_type: 'custom',
    options: values.tickets.map((t) => ({
      ticket_id: t.id,
      price_per_unit: t.price_per_unit || 0,
    })),
  },
});

type ParentValues = { multiple_tickets?: MultipleTicketsFormValues };

type Props = { formIsDisabled: boolean; currencySymbol: string; activityLabel: string };

export const MultipleTicketsPricingFields: React.FC<Props> = ({ formIsDisabled, currencySymbol, activityLabel }) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<ParentValues>();
  const mt = values.multiple_tickets;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);

  if (!mt) return null;

  return (
    <Grid container flexDirection="column" gap={2}>
      <Typography variant="body2" color="text.secondary">
        {t('editOrderPage.multipleTickets.description')}
      </Typography>

      <FormLabelField
        labelFieldName="multiple_tickets.label"
        label={t('editOrderPage.multipleTickets.widgetLabel')}
        disabled={formIsDisabled}
      />

      <Grid container gap={2}>
        <FormTextField
          isFieldUpdate
          fieldName="multiple_tickets.min_total_participants"
          label={t('editOrderPage.multipleTickets.minTotal')}
          disabled={formIsDisabled}
          type="number"
          width={180}
          min={0}
        />
        <FormTextField
          isFieldUpdate
          fieldName="multiple_tickets.max_total_participants"
          label={t('editOrderPage.multipleTickets.maxTotal')}
          disabled={formIsDisabled}
          type="number"
          width={180}
          min={0}
        />
      </Grid>

      <FormCheckbox
        fieldName="multiple_tickets.dashboard_no_limit"
        label={t('editOrderPage.multipleTickets.dashboardNoLimit')}
        disabled={formIsDisabled}
        isFieldUpdate
      />

      <Typography variant="subtitle2" fontWeight={600}>
        {t('editOrderPage.multipleTickets.ticketsHeader')}
      </Typography>

      <FieldArray name="multiple_tickets.tickets">
        {({ push, remove }) => (
          <Grid container flexDirection="column" gap={2}>
            {mt.tickets.map((ticket, index) => {
              const path = `multiple_tickets.tickets[${index}]`;
              const showUnboundedWarning =
                !ticket.counts_toward_total_capacity && (ticket.capacity === undefined || ticket.capacity === null);
              return (
                <Grid
                  key={ticket.id}
                  container
                  flexDirection="column"
                  gap={2}
                  sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
                  <Grid container justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" fontWeight={600}>
                      {t('editOrderPage.multipleTickets.ticket')} {index + 1}
                    </Typography>
                    <IconButton size="small" onClick={() => setPendingDeleteIndex(index)} disabled={formIsDisabled}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Grid>
                  <FormLabelField
                    labelFieldName={`${path}.label`}
                    label={t('editOrderPage.multipleTickets.ticketName')}
                    disabled={formIsDisabled}
                  />
                  <Grid container gap={2}>
                    <FormTextField
                      isFieldUpdate
                      fieldName={`${path}.price_per_unit`}
                      label={t('editOrderPage.multipleTickets.pricePerUnit')}
                      disabled={formIsDisabled}
                      type="number"
                      width={180}
                      min={0}
                      endAdornment={currencySymbol}
                    />
                    <FormTextField
                      isFieldUpdate
                      fieldName={`${path}.capacity`}
                      label={t('editOrderPage.multipleTickets.capacity')}
                      caption={t('editOrderPage.multipleTickets.capacityCaption', { activityLabel })}
                      disabled={formIsDisabled}
                      type="number"
                      width={180}
                      min={1}
                    />
                  </Grid>
                  <Grid
                    container
                    alignItems={isSmallScreen ? 'flex-start' : 'center'}
                    gap={isSmallScreen ? 0.5 : 1}
                    flexWrap="nowrap"
                    flexDirection={isSmallScreen ? 'column' : 'row'}
                    sx={
                      isSmallScreen
                        ? { border: '1px solid', borderColor: 'mb.control', borderRadius: '6px', p: 1 }
                        : undefined
                    }>
                    <Grid item sx={{ width: 'fit-content', flexShrink: 0 }}>
                      <FormCheckbox
                        fieldName={`${path}.counts_toward_total_capacity`}
                        label={t('editOrderPage.multipleTickets.countsTowardTotal')}
                        disabled={formIsDisabled}
                        isFieldUpdate
                      />
                    </Grid>
                    <Typography variant="caption" color="text.secondary">
                      {t('editOrderPage.multipleTickets.countsTowardTotalHelp', { activityLabel })}
                    </Typography>
                  </Grid>
                  {showUnboundedWarning && (
                    <Typography variant="caption" color="warning.main">
                      {t('editOrderPage.multipleTickets.unboundedWarning')}
                    </Typography>
                  )}
                </Grid>
              );
            })}
            <Button
              startIcon={<Add />}
              onClick={() =>
                push({
                  id: generateTicketId(),
                  label: {},
                  counts_toward_total_capacity: true,
                  price_per_unit: 0,
                })
              }
              disabled={formIsDisabled}
              variant="outlined"
              color="inherit"
              size="small"
              sx={{ alignSelf: 'flex-start' }}>
              {t('editOrderPage.multipleTickets.addTicket')}
            </Button>
            <ConfirmationDialog
              isOpen={pendingDeleteIndex !== null}
              onClose={(confirmed) => {
                if (confirmed && pendingDeleteIndex !== null) {
                  remove(pendingDeleteIndex);
                }
                setPendingDeleteIndex(null);
              }}
              title={t('editOrderPage.multipleTickets.deleteConfirmTitle')}
              content={t('editOrderPage.multipleTickets.deleteConfirmBody')}
              ctaText={t('editOrderPage.multipleTickets.deleteConfirmCta')}
              confirmButtonColor="error"
            />
          </Grid>
        )}
      </FieldArray>
    </Grid>
  );
};
